import type { AnalysisCalendarPeriod } from "./analysis-date-range";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ShiftFormManagerDashboardFilters } from "../../../shared/shift-production-form/contracts";
import { getShiftFormManagerDashboard } from "../shift-production-form/shift-production-form-gateway";
import { analysisFactoryFilters } from "./analysis-factory-summary";
import { cachedAnalysisFactoryReports, loadAnalysisFactoryReports, pendingAnalysisReport, type AnalysisFactoryReports } from "./analysis-factory-reports";

export function useAnalysisFactoryReports(actorScope: string, filters: ShiftFormManagerDashboardFilters, period?: AnalysisCalendarPeriod, testMode = false) {
  const { dateFrom, dateTo, status, shift, limit } = filters;
  const scope = useMemo(() => analysisFactoryFilters({ dateFrom, dateTo, status, shift, limit }), [dateFrom, dateTo, status, shift, limit]);
  const scopeKey = JSON.stringify([scope, period ?? null]);
  const [revision, setRevision] = useState(0);
  const [snapshot, setSnapshot] = useState<{ key: string; revision: number; reports: AnalysisFactoryReports } | null>(() => {
    const reports = cachedAnalysisFactoryReports(actorScope, scope, period);
    return reports ? { key: scopeKey, revision: 0, reports } : null;
  });
  useEffect(() => {
    const cancel = loadAnalysisFactoryReports(actorScope, scope, filters => getShiftFormManagerDashboard(filters, testMode), reports => setSnapshot({ key: scopeKey, revision, reports }), period);
    return cancel;
  }, [actorScope, scope, scopeKey, revision, period, testMode]);
  const reload = useCallback(() => setRevision(value => value + 1), []);
  const cached = cachedAnalysisFactoryReports(actorScope, scope, period);
  const reports = snapshot?.key === scopeKey && snapshot.revision === revision
    ? snapshot.reports
    : cached ?? { current: pendingAnalysisReport(), previous: pendingAnalysisReport() };
  return { ...reports, reload };
}
